<template>
  <section class="d-flex flex-column h-100">
    <fixed-header
      title="Sales Orders"
      :records="pageInfo.totalItems"
      hideBackBtn
    >
      <add-sales-order v-if="checkClaim(CAN_ADD_SALES_ORDER)" />
    </fixed-header>

    <div class="card h-50">
      <div class="card-body d-flex flex-column">
        <div class="d-flex justify-content-start align-items-end mb-2">
          <b-form-group label="Status" class="mr-2">
						<b-form-select v-model="filters.statusId" @change="onSearch" style="min-width: 100px;">
              <option :value="null">All</option>
              <option v-for="option in sortSelect(statusOptions)" :key="option.name" :value="option.id">
                {{option.name}}</option>
						</b-form-select>
					</b-form-group>
          <b-form-group label="Sales Order Number" class="mr-2">
            <b-form-input v-model="filters.soNumber" @input="onSearch" size="sm" />
          </b-form-group>
          <b-form-group label="Customer Purchase Order" class="mr-2">
            <b-form-input v-model="filters.poNumber"  @input="onSearch" size="sm" />
          </b-form-group>
          <b-form-group label="Customer PN" class="mr-2">
            <b-form-input v-model="filters.customerPn" @input="onSearch" size="sm" />
          </b-form-group>
          <b-form-group label="Company" class="mr-2">
            <b-form-input v-model="filters.companyName" @input="onSearch" size="sm" />
          </b-form-group>
          <b-form-group label="Needs auth" class="mr-2" style="margin-bottom: 5px;">
            <b-form-checkbox v-model="filters.needsAuthorization" :indeterminate.sync="filters.needsAuthorization == null" @input="onSearch" />
          </b-form-group>
          <b-form-group label="Sales Rep" class="mr-2" v-if="checkClaim(CAN_VIEW_ALL_SALES_ORDERS)">
            <b-form-select v-model="filters.salesRep" @change="onSearch">
              <option :value="null">All</option>
              <option
                v-for="(selectRep, indexRep) in sortSelect(salesReps)"
                :key="indexRep"
                :value="selectRep.id"
              >{{ selectRep.name }}</option>
            </b-form-select>
          </b-form-group>
          <date-range-picker :dateFrom="filters.dateFrom" :dateTo="filters.dateTo" @handleSearch="onSearch"></date-range-picker>

          <b-button class="mr-2" size="sm" @click="onClear">Clear filters</b-button>
        </div>
        <grid-component
          isResizable
          isReorderable
          isSortable
          :canEdit="checkClaim(CAN_EDIT_SALES_ORDER)"
          :canDelete="checkClaim(CAN_DELETE_SALES_ORDERS)"
          :gridId="gridId"
          :pageable="pageable"
          :skip="pageInfo.pageSize * (pageInfo.pageNumber - 1)"
          :take="pageInfo.pageSize"
          :itemData="itemData"
          :pageSizes="pageInfo.pageSize"
          :totalItems="pageInfo.totalItems"
          :fields="fields"
          :sortBy="pageInfo.sortBy"
          :sortOrder="pageInfo.sortOrder"
          :sortMethod="sortGrid"
          :onEdit="pushToDetails"
          :onDelete="onDelete"
          :handleRowClick="onRowClick"
          :styleCustom="'height: 335px'"
          @getData="onGetData"
          :isTblLoader="tblLoader"
        />
      </div>
    </div>
    <b-card>
      <b-tabs>
        <b-tab active>
          <template v-slot:title>
            Sales Order Items &nbsp;<b-badge variant="primary">{{ salesOrderLines.length }}</b-badge>
          </template>
          <sales-order-lines
            :salesOrderId="selectedRowItemId"
            :checkRowItemIds.sync="checkedRowItemIds"
            @onShowEdit="onShowEdit"
          />
        </b-tab>
      </b-tabs>
    </b-card>

    <div id="add-part-popup" class="d-inline pull-left">
      <b-modal id="add-part-popup" ref="add-part-popup" size="xl" ok-title="Save" scrollable>
        <template slot="modal-header">
          <h5 class="modal-title">Edit Line Detail</h5>
        </template>
        <line-details
          ref="editLineDetails"
          :lineObj="salesOrderLine"
          @partChange="onPartChange"
          @lineChange="onLineChange"
          @headerChange="onHeaderChange"
          @line-validation="isLineInvalid = $event"
        />
        <template slot="modal-footer">
          <b-button @click="$bvModal.hide('add-part-popup')">Cancel</b-button>
          <b-button v-if="checkClaim(CAN_EDIT_SALES_ORDER)" class="mr-2" variant="primary" @click="savePartForm" :disabled="isSavingLine">
            <i v-if="isSavingLine" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Save</b-button>          
        </template>

      </b-modal>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import debounce from 'lodash/debounce';

import { dateTime, date } from '@/shared/filters';
import { GRID_PAGE_SIZES, GRID_ID, SALES_ORDER_STATUS} from '@/shared/constants';
import { prepareColumnsData } from '@/shared/helpers';
import GridSettings from '@/shared/components/GridSettings';

import AddSalesOrder from './components/add';
import SalesOrderLines from './components/SalesOrderLines';
import RequirementDetails from '@/shared/components/RequirementDetails/index';
import LineDetails from '@/shared/components/LineDetails/index';
import FixedHeader from '@/shared/components/FixedHeader/indexNew';
import gridFields from './fields';
import GridComponent from '@/shared/components/GridComponent';

import DateRangePicker from '@/shared/components/DateRangePicker/IndexNew';

const SEARCH_DEBOUNCE_TIME = 750;

export default {
  components: {
    GridSettings,
    AddSalesOrder,
    SalesOrderLines,
    FixedHeader,
    DateRangePicker,
    RequirementDetails,
    GridComponent,
    LineDetails
  },
  metaInfo: () => ({
    title: 'Sales Orders',
  }),
  data() {
    return {
      prepareColumnsData,
      date,
      filtersVisible: false,
      fields: gridFields,
      gridId: GRID_ID.SALES_ORDERS,
      pageable: {
        pageSizes: GRID_PAGE_SIZES,
      },
      selectedRowItemId: 0,
      // statusOptions:[
      //   {id: 334, name:'Backorders'},
      //   {id: 212, name:'Open'},
      //   {id: 213, name:'Closed/Shipped'},
      // ],
      statusOptions: SALES_ORDER_STATUS,
      checkedRowItemIds: [],
      salesOrderLine: {},
      mergedObj: {
        salesOrderId: this.selectedRowItemId
      },
      isSavingLine: false,
      isLineInvalid: false
    };
  },
  computed: {
    ...mapState('salesReps', ['salesReps']),
    ...mapGetters('salesOrders', [
      'salesordersList',
      'pageInfo',
      'lastOpenedId',
      'filters',
      'tblLoader'
    ]),
    ...mapGetters('account', ['gridSettingsById']),
    ...mapGetters('salesOrderLines', ['salesOrderLinesById']),
    salesOrderLines() {
      return this.salesOrderLinesById(this.selectedRowItemId);
    },
    gridSettings() {
      return this.gridSettingsById(this.gridId);
    },
    itemData() {
      // if(this.salesordersList) {
      //   return this.salesordersList.map((salesorder) => ({ ...salesorder, selected: salesorder.id === this.selectedRowItemId }));
      // } else {
      //   return [];
      // }

      if (this.salesordersList == undefined || !this.salesordersList.length) return [];
      return this.salesordersList.map((salesorder) => ({ ...salesorder, selected: salesorder.id === this.selectedRowItemId }));
    },
  },
  filters: {
    dateTime,
    date
  },
  methods: {
    // ...mapMutations('salesOrders', [
    //   'setLastOpenedId',
    //   'setFiltersClear',
    // ]),
    ...mapMutations('salesOrders', ['setItem']),
    ...mapActions('salesOrders', [
      'getAll',
      'getByParams',
      'sortGrid',
      'search',
      'deleteSalesOrder',
    ]),
    ...mapActions('account', ['saveGridSettings']),
    ...mapActions('salesOrderLines', [ 'updateSalesOrderLine', 'getBySalesOrderId' ]),
    onSearch: debounce(function (data = false) {
      if(typeof data === 'object') { Object.assign(this.filters,data) }
      this.selectedRowItemId = 0;
      this.search(this.filters);
    }, SEARCH_DEBOUNCE_TIME),
    onClear(callSearch=true) {
      // Object.keys(this.filters).forEach(filterName => {
			// 	this.filters[filterName] = null;
      // });
      // if(callSearch){this.search(this.filters);}
      //
      // this.$refs.dateRangePicker.setDateRangeAll(); //Clear date range picker

      this.clearObject(this.filters);
      if (callSearch) {
        this.search(this.filters);
      }
    },
    async onGetData(params) {
      await this.getAll(params);
    },
    onRowClick(row) {
     // console.log('Current Selected Sales Order Row: ' + this.selectedRowItemId);
      if(this.selectedRowItemId != row.dataItem.id){
        this.selectedRowItemId = row.dataItem.id;
       // console.log('Select Sales Order Row: ' + this.selectedRowItemId);
        const { id, detailsVisible } = row.dataItem;
        //this.select({ id, isChecked: !detailsVisible });
      }
    },
    pushToDetails(row) {
      // this.setLastOpenedId(row.id);
      this.setItem({ resource: 'lastOpenedId', payload: row.id });
      this.$router.push({
        name: 'salesorders.edit',
        params: { id: row.id },
      });
    },
    async onDelete(salesOrder) {
      const deleteConfirmed = await this.openConfirmBox({message: `Are you sure you want to delete Sales Order #${salesOrder.id}?`});
      if (deleteConfirmed) {
        await this.deleteSalesOrder(salesOrder.id)
        this.getAll({});
      }
    },
    onShowEdit(salesOrderLine) {
      this.salesOrderLine = salesOrderLine;

      this.$refs['add-part-popup'].show();
    },
    onPartChange(partObj) {
      this.mergedObj = {
        ...this.mergedObj,
        ...partObj
      };
    },
    onLineChange(lineObj) {
      this.mergedObj = {
        ...this.mergedObj,
        ...lineObj
      };
    },
    onHeaderChange(headerObj) {
      this.mergedObj = {
        ...this.mergedObj,
        ...headerObj
      };
    },
    async savePartForm(bvModalEvt) {
      await this.$refs.editLineDetails.getLineValidation();
      if(!this.isLineInvalid) {
        this.isSavingLine = true;
        this.mergedObj.salesOrderId = this.selectedRowItemId;
        await this.updateSalesOrderLine(this.mergedObj);
        this.isSavingLine = false;
        this.$bvModal.hide('add-part-popup');
        await this.getBySalesOrderId(this.selectedRowItemId);        
      }      
    },
  },
  watch: {
    async salesordersList() {
      //This function sets the first row as the selected row
      this.selectedRowItemId = await this.setSelectedRowId(this.salesordersList,this.selectedRowItemId,this.lastOpenedId);
      /*//This function sets the first row as the selected row
      if(this.salesordersList && this.salesordersList.length > 0 && this.selectedRowItemId === 0 && !this.lastOpenedId) {
        this.selectedRowItemId = this.salesordersList[0].id;
        const { id, detailsVisible } = this.salesordersList[0];
        //this.select({ id, isChecked: !detailsVisible });
      }*/
    },
  },
  async mounted() {
    if('clear' in this.$route.query) {
      this.pageInfo.pageNumber = 1;
      this.onClear(false)
      this.clearRoute();
    } else {
      if(this.lastOpenedId) {
        this.selectedRowItemId = this.lastOpenedId;
      }

      if('statisticFilter' in this.$route.params && this.$route.params.statisticFilter){
        this.clearObject(this.filters);
      }

      if('needsAuthorization' in this.$route.params && this.$route.params.needsAuthorization) {
        this.filters.needsAuthorization = true;
        delete this.$route.params.needsAuthorization;
      }

      if('salesOrderStatus' in this.$route.params) {
        this.filters.statusId = this.$route.params.salesOrderStatus;
        delete this.$route.params.salesOrderStatus;
      }

      if('dateRangeFilter' in this.$route.params) {
        let from_date = this.$route.params.dateRangeFilter.dateFrom;
        let to_date = this.$route.params.dateRangeFilter.dateTo;

        this.filters.dateFrom = from_date;
        this.filters.dateTo = to_date;

        delete this.$route.params.dateRangeFilter;
      }

      if('salesPersonFilter' in this.$route.params && this.$route.params.salesPersonFilter) {
        this.filters.salesRep = this.$route.params.salesPersonFilter;
        delete this.$route.params.salesPersonFilter;
      }

      if('statisticFilter' in this.$route.params && this.$route.params.statisticFilter){
        delete this.$route.params.statisticFilter;
        this.search(this.filters);
      }else {
        this.setItem({
          resource: 'pageInfo',
          payload: {...this.pageInfo, ...this.gridSettings.sort, ...this.gridSettings.pageSize}
        });
        await this.getAll({});
      }
    }
  },
  beforeDestroy() {
    //this.select({ id: null, isChecked: false });
  },

};
</script>

<style lang="scss">
@import '@/assets/scss/_colors.scss';

.highlight {
  background: $rowSelection !important;
}

.b-pagination {
  justify-content: center;
}

.tools > * {
  margin: 0 0 0 3px;
}

#KendoGrid .k-virtual-scrollable-wrap table {
  min-width: 800px;
}
</style>
